import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  Typography,
  colors,
  makeStyles,
} from "@material-ui/core";
import React, { useEffect, useRef, useState } from "react";

import { ProfileDetailsCardProps } from "../../types";
import { Skeleton } from "@material-ui/lab";
import { startCase } from "lodash";
import AdditionalLanguageFlag from "./AdditionalLanguageFlag";

const useStyles = makeStyles(theme => ({
  cardContent: {
    padding: 0,
  },
  item: {
    padding: theme.spacing(3, 3, 3, 2),
  },
  title: {
    padding: theme.spacing(0),
    marginBottom: theme.spacing(2),
  },
  subTitle: {
    fontWeight: 300,
    fontSize: "15px",
  },
  cardActions: {
    display: "flex",
    justifyContent: "space-between",
  },
  addButton: {
    margin: theme.spacing(0, 0, 1, 1),
  },
  updateButton: {
    justifyContent: "center",
    weight: "300px",
  },
  bioContainer: {
    display: "flex",
    flexDirection: "column",
  },
  bioLabel: {
    fontSize: "14px",
    marginBottom: theme.spacing(1),
  },
  bioText: {
    fontSize: "14px",
    overflowWrap: "break-word",
  },
  gridRow: {
    borderBottom: `1px solid ${colors.blueGrey[50]}`,
    width: "100%",
    display: "flex",
    padding: theme.spacing(3, 0),
  },
  gridLabel: {
    minWidth: theme.spacing(10),
    paddingRight: theme.spacing(2),
  },
  gridValue: {
    flex: 1,
    "&:last-child": {
      marginRight: 0,
    },
  },
  bioGrid: {
    borderBottom: `1px solid ${colors.blueGrey[50]}`,
    width: "100%",
    padding: theme.spacing(3, 0),
  },
  skeleton: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    paddingTop: theme.spacing(2),
  },
  flagsContainer: {
    display: "flex",
    whiteSpace: "nowrap",
    overflowX: "auto",
    flex: 1,
    "& > *": {
      marginRight: theme.spacing(1),
    },
  },
}));

const ProfileDetails: React.FC<ProfileDetailsCardProps> = ({
  displayName,
  additionalLanguages,
  bio,
  handleAddDetailsOnClick,
  loading,
}) => {
  const classes = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const [startHideIndex, setStartHideIndex] = useState<number | undefined>(
    undefined,
  );

  const updateStartHideIndex = () => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const flags = Array.from(container.children);
    const approxCounterWidth = 20;
    const containerRight =
      container.getBoundingClientRect().right - approxCounterWidth;
    const startHideIndex = flags.findIndex(
      flag => flag.getBoundingClientRect().right > containerRight,
    );
    setStartHideIndex(startHideIndex >= 0 ? startHideIndex : undefined);
  };

  useEffect(() => {
    updateStartHideIndex();
  }, [additionalLanguages]);

  const truncateFlagName = (name: string, maxLength: number) =>
    name.length > maxLength ? `${name.slice(0, maxLength)}...` : name;

  const renderAdditionalLanguages = (): JSX.Element | JSX.Element[] => {
    if (!additionalLanguages?.length) {
      return <span>-</span>;
    }
    const languageFlags = additionalLanguages.map((language, index) => {
      const truncatedLanguage =
        index === 0 ? truncateFlagName(language, 15) : language;
      return (
        <AdditionalLanguageFlag
          key={index}
          label={truncatedLanguage}
          hidden={startHideIndex && index >= startHideIndex ? true : false}
        />
      );
    });
    return (
      <>
        {languageFlags}
        {startHideIndex !== undefined && (
          <AdditionalLanguageFlag
            label={`+${additionalLanguages.length - startHideIndex}`}
            hidden={false}
          />
        )}
      </>
    );
  };

  return (
    <Card data-test="profile-details">
      <CardContent className={classes.cardContent}>
        <Grid container direction={"row"} wrap="wrap" justify="space-between">
          <Grid key="1" item sm={12} xs={12} className={classes.item}>
            <Typography
              variant="h2"
              color="textSecondary"
              className={classes.title}
            >
              Profile details
            </Typography>
            <Typography
              variant="h3"
              color="textPrimary"
              className={classes.subTitle}
            >
              This information will be shown on your public business profile.
            </Typography>
            {loading ? (
              <Grid className={classes.skeleton}>
                {[...Array(3)].map((_, idx) => (
                  <Box key={idx} mb={2}>
                    <Skeleton key={idx} width="100%" height="35px" />
                  </Box>
                ))}
              </Grid>
            ) : (
              <>
                <Grid container className={classes.gridRow}>
                  <Grid item className={classes.gridLabel}>
                    {startCase("display name")}:
                  </Grid>
                  <Grid
                    item
                    className={classes.gridValue}
                    data-test={`data-table-value-displayName`}
                  >
                    {displayName ?? "-"}
                  </Grid>
                </Grid>

                <Grid container className={classes.gridRow}>
                  <Grid item className={classes.gridLabel}>
                    {startCase("additional Languages")}:
                  </Grid>
                  <Grid
                    item
                    className={classes.flagsContainer}
                    data-test={`data-table-value-additionalLanguages`}
                    ref={containerRef}
                  >
                    {renderAdditionalLanguages()}
                  </Grid>
                </Grid>

                <Grid container className={classes.bioGrid}>
                  <Grid item xs={12}>
                    <div className={classes.bioContainer}>
                      <Typography
                        variant="body1"
                        color="textPrimary"
                        className={classes.bioLabel}
                      >
                        {startCase("bio")}:
                      </Typography>
                      <Box>
                        {bio ? (
                          bio.split("\\n").map((paragraph, index) => (
                            <Grid key={index} item xs={12}>
                              <Typography
                                variant="body1"
                                color="textSecondary"
                                className={classes.bioText}
                              >
                                {paragraph}
                              </Typography>
                            </Grid>
                          ))
                        ) : (
                          <Grid item xs={12}>
                            <Typography
                              variant="body1"
                              color="textSecondary"
                              className={classes.bioText}
                            >
                              -
                            </Typography>
                          </Grid>
                        )}
                      </Box>
                    </div>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      </CardContent>
      <CardActions className={classes.cardActions}>
        {loading ? (
          <div></div>
        ) : (
          <>
            {displayName || additionalLanguages || bio ? (
              <Grid container className={classes.updateButton}>
                <Grid item>
                  <Button
                    className={classes.updateButton}
                    variant="text"
                    color="secondary"
                    onClick={handleAddDetailsOnClick}
                  >
                    Update
                  </Button>
                </Grid>
              </Grid>
            ) : (
              <Grid container className={classes.addButton}>
                <Grid item>
                  <Button
                    className={classes.addButton}
                    variant="contained"
                    color="secondary"
                    onClick={handleAddDetailsOnClick}
                  >
                    Add details
                  </Button>
                </Grid>
              </Grid>
            )}
          </>
        )}
      </CardActions>
    </Card>
  );
};
export default ProfileDetails;
